/**
 * Connected admin users with data fetching and mutations.
 *
 * Gets current user from Zero cache for permission checks.
 * Optional confirmDelete for platform-specific dialogs.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { createUsersApi } from "@animus/frontend-features/admin/users";
import { useAuthStore } from "@animus/frontend-features/auth-store";
import {
  useApiConfig,
  useAuthStrategy,
} from "@animus/frontend-features/config";

import type { AdminUser, Role } from "./types.ts";
import { AdminUsers } from "./admin-users.tsx";

interface AdminUsersConnectedProps {
  confirmDelete?: (message: string) => Promise<boolean>;
}

function AdminUsersConnected({
  confirmDelete = (msg) =>
    // biome-ignore lint/suspicious/noAlert: default fallback
    Promise.resolve(window.confirm(msg)),
}: AdminUsersConnectedProps) {
  const { baseUrl } = useApiConfig();
  const auth = useAuthStrategy();
  const api = useMemo(() => createUsersApi(baseUrl, auth), [baseUrl, auth]);

  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === "admin";

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await api.listUsers({ limit: 100 });

    if (result.isErr) {
      setError(result.error.message ?? "Failed to fetch users");
      setIsLoading(false);
      return;
    }

    setUsers(
      result.value.users.map((u) => ({
        id: u.id,
        email: u.email,
        name: u.name ?? null,
        role: u.role ?? "user",
        banned: u.banned ?? false,
        banReason: u.banReason ?? null,
        createdAt: u.createdAt,
      }))
    );
    setIsLoading(false);
  }, [api]);

  useEffect(() => {
    fetchUsers().catch(() => {
      // Error already handled in fetchUsers
    });
  }, [fetchUsers]);

  async function handleBan(userId: string, reason?: string) {
    const result = await api.banUser(userId, reason);
    if (result.isErr) {
      setError(result.error.message ?? "Failed to ban user");
      return;
    }
    await fetchUsers();
  }

  async function handleUnban(userId: string) {
    const result = await api.unbanUser(userId);
    if (result.isErr) {
      setError(result.error.message ?? "Failed to unban user");
      return;
    }
    await fetchUsers();
  }

  async function handleSetRole(userId: string, role: Role) {
    const result = await api.setRole(userId, role);
    if (result.isErr) {
      setError(result.error.message ?? "Failed to set role");
      return;
    }
    await fetchUsers();
  }

  async function handleDelete(userId: string) {
    const confirmed = await confirmDelete(
      "Are you sure you want to delete this user?"
    );
    if (!confirmed) {
      return;
    }
    const result = await api.removeUser(userId);
    if (result.isErr) {
      setError(result.error.message ?? "Failed to remove user");
      return;
    }
    await fetchUsers();
  }

  return (
    <AdminUsers
      {...(currentUser && { currentUserId: currentUser.id })}
      error={error}
      isAdmin={isAdmin}
      isLoading={isLoading}
      onBan={handleBan}
      onDelete={handleDelete}
      onRefresh={fetchUsers}
      onSetRole={handleSetRole}
      onUnban={handleUnban}
      users={users}
    />
  );
}

export { AdminUsersConnected, type AdminUsersConnectedProps };
